import { BrowserModule } from '@angular/platform-browser';
import { NgModule, Injectable, APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CircleTimerModule } from './modules/circle-timer/src/public-api';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SignInComponent } from './component/sign-in/sign-in.component';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ForgotPasswordComponent } from './component/forgot-password/forgot-password.component';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}
import { RangePipe } from './pipe/range-pipe/range.pipe';
import { FilterPipe } from './pipe/list-filter-pipe/list-filter-pipe';
import { FilterByKeyPipe } from './pipe/list-filterbykey-pipe/list-filterbykey-pipe';
import { DispatchDashboardComponent } from './component/dashboard/dashboard/dashboard.component';
import { AgmCoreModule, LAZY_MAPS_API_CONFIG, LazyMapsAPILoaderConfigLiteral } from '@agm/core';
import { StaffMainComponent } from './component/staffs/staff-main/staff-main.component';
import { StaffsCreateComponent } from './component/staffs/staffs-create/staffs-create.component';
import { TokenInterceptorService } from './service/token-interceptor-service/token-interceptor.service';
import { EditUserProfileComponent } from './component/user profile/edit-user-profile/edit-user-profile.component';
import { AuthorizationGuard } from './service/auth-guard/authorization-guard.service';
// we are using this in edit profile for all time zone list
import { AutocompleteComponent } from './component/common-components/goolge-places-selecters/google-places.component';
// import { TripsHistoryComponent } from './component/dispatch-center/trips-history/trips-history.component';
import { DriverListComponent } from './component/drivers/driver-list/driver-list.component';
import { RolePipe } from './pipe/role-value-filter/role.pipe';
import { AccountStatusPipe } from './pipe/account-status-value-filter/account-status.pipe';
import { I18nModule } from './directives/i18n/i18n.module';
import { CreateDriverComponent } from './component/drivers/create-driver/create-driver.component';
import { DriverProfileDashboardComponent } from './component/drivers/driver-profile-dashboard/driver-profile-dashboard.component';
import { ImageModalComponent } from './modals/image-modal/image-modal.component';
import { CamelCasePipe } from './pipe/camelcase.filter/camelcase.filter.pipe';
import { CorporateuserComponent } from './component/corporate-user/corporate-user-list/corporate-user.component';
import { CorporateAddUserComponent } from './component/corporate-user/corporate-add-user/corporate-add-user.component';
import { FleetListComponent } from './component/fleet/fleet-list/fleet-list.component';
import { CreateFleetComponent } from './component/fleet/create-fleet/create-fleet.component';
import { DriverDistributionsComponent } from './component/driver-distributions/driver-distributions-list/driver-distributions.component';
// tslint:disable-next-line: max-line-length
import { DriverDistributionsCreateComponent } from './component/driver-distributions/driver-distributions-create/driver-distributions-create.component';
import { DriverAlertComponent } from './component/driver-alert/driver-alert/driver-alert.component';
import { HelpingMaterialListComponent } from './component/helping-material/helping-material-list/helping-material-list.component';
import { UrlSafePipe } from './pipe/url-safe-pipe/url-safe.pipe';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ParseTripsComponent } from './component/broker-parser/parse-trips/parse-trips.component';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';
// import { PlannerEffects } from './ngrx/store/effects/planner-effects';
import { UnassignedComponent } from './component/assigned-trips/unassigned/unassigned.component';
import { StagingComponent } from './component/assigned-trips/staging/staging.component';
import { AvailabilityMatrixComponent } from './component/availability-matrix/availability-matrix.component';
import { AvailabilityCoverageComponent } from './component/availability-matrix/coverage/availability-coverage.component';
import { VerifiedTripLogsComponent } from './component/billing/verified-trip-logs/verified-trip-logs.component';
import { NewTripComponent } from './component/assigned-trips/new-trip/new-trip.component';
import { GooglePlaceDirective } from './directives/google-places-directive/google-place.directive';
import { NemtStatisticsComponent } from './component/kpis/nemt-statistics/nemt-statistics.component';
import { NemtReportComponent } from './component/kpis/nemt-report/nemt-report.component';

import { DynamicFareListComponent } from './component/dynamic-fare/dynamic-fare-list/dynamic-fare-list.component';
import { DynamicFareAddComponent } from './component/dynamic-fare/dynamic-fare-add/dynamic-fare-add.component';
import { SocketIoModule, SocketIoConfig, Socket } from 'ngx-socket-io';
import { urls } from './utils/url-utils';
import { SocketService } from './service/socket-service/socket-service.service';
import { AgmOverlays } from 'agm-overlays';
import { EarningsComponent } from './component/reports/earnings/earnings.component';
import { TripFilterPipe } from './pipe/trip-list-filter-pipe/trip-filter.pipe';
import { TripAssignModalComponent } from './modals/trip-assign-modal/trip-assign-modal.component';
import { ThirdPartyCompaniesTripAssignModalComponent } from './modals/third-party-companies-trip-assign-modal/third-party-companies-trip-assign-modal.component';
import { TimeFormatDirective } from './directives/time-format-directive/time-format.directive';
import { CompanyNoteModalComponent } from './modals/company-note-modal/company-note-modal.component';
import { CancelTripModalComponent } from './modals/cancel-trip-modal/cancel-trip-modal.component';
import { PullBackFarmoutModalComponent } from './modals/pull-back-farmout-modal/pull-back-farmout-modal.component';
import { TimePipe } from './pipe/time-pipe/time.pipe';
import { AgmDirectionModule } from 'agm-direction';
import { TripDetailDialogComponent } from './component/common-components/trip-detail-dialog/trip-detail-dialog.component';

import { SignaturePadModule } from 'angular2-signaturepad';
import { CommonAlertComponent } from './component/common-components/common-alert/common-alert.component';
import { DriverBreakdownComponent } from './component/kpis/driver-breakdown/driver-breakdown.component';
import { SweepTripsModalComponent } from './modals/sweep-trips-modal/sweep-trips-modal.component';
import { TimeagoModule } from 'ngx-timeago';
import { MySocketWrapper } from './service/socket-service/socket-wrapper';
import { NgxUiLoaderConfig, NgxUiLoaderHttpModule, NgxUiLoaderModule } from './modules/ngx-ui-loader/src/public-api';
import { DatePipe } from './pipe/date-pipe/date.pipe';
import { OwlModule } from 'ngx-owl-carousel';
import { NotificationDetailsComponent } from './notification/notification-details/notification-details.component';
// import { AccidnetsListComponent } from './component/accidents/accidnets-list/accidnets-list.component';
// import { AddAccidentComponent } from './component/accidents/add-accident/add-accident.component';
import { HelpingMaterialPdfComponent } from './component/helping-material/helping-material-pdf/helping-material-pdf.component';
// import { AccidentDetailInTailwindFormComponent } from './component/accidents/accident-detail-in-tailwind-form/accident-detail-in-tailwind-form.component';
import { LogsComponent } from './component/offered-trips/logs/logs.component';
import { MemberListComponent } from './component/members/member-list/member-list.component';
import { CreateMemberComponent } from './component/members/create-member/create-member.component';
import { OutstandingComponent } from './component/assigned-trips/outstanding/outstanding.component';
import { DriverInvoicesComponent } from './component/drivers/driver-invoices/driver-invoices.component';
import { DirtRoadComponent } from './component/assigned-trips/dirt-road/dirt-road.component';
import { DriverTimelinesComponent } from './component/drivers/driver-timelines/driver-timelines.component';
import { TripDetailModelComponent } from './modals/trip-detail-model/trip-detail-model.component';
import { OutstandingTripsModalComponent } from './modals/outstanding-trips-modal/outstanding-trips-modal.component';
import { ZDatesComponent } from './component/page-not-found/z-dates.component';
import { SideNavComponent } from './component/shared/layout/side-nav/side-nav.component';
import { HeaderComponent } from './component/shared/layout/header/header.component';
import { DriverEarningModalComponent } from './component/reports/earnings/driver-earning-modal/driver-earning-modal.component';
import { NotificationsSlideoutComponent } from './component/shared/slideouts/notifications-slideout/notifications-slideout.component';
import { DispatchInprogressTripsSlideoutComponent } from './component/shared/slideouts/dispatch-inprogress-trips-slideout/dispatch-inprogress-trips-slideout.component';
import { SortByDatePipe } from './pipe/sort-by-date/sort-by-date.pipe';
import { OutsideClickDirective } from './directives/outside-click-directive/outside-click.directive';
import { BrokerDropdownComponent } from './component/shared/broker-dropdown/broker-dropdown.component';
import { TripActionsDropdownComponent } from './component/shared/trip-actions-dropdown/trip-actions-dropdown.component';
import { AiAssistantConversationModalComponent } from './modals/ai-assistant-conversation-modal/ai-assistant-conversation-modal.component';
import { ClockTimePipe } from './pipe/clock-time/clock-time.pipe';
import { EditTripModalComponent } from './modals/edit-trip-modal/edit-trip-modal.component';
import { ShiftOverrideModalComponent } from './modals/shift-override-modal/shift-override-modal.component';
import { ToastrModule } from 'ngx-toastr';
import { OfferedComponent } from './component/assigned-trips/offered/offered.component';
import { BrokerSettingComponent } from './component/broker-parser/broker-setting/broker-setting.component';
import { BrokerSettingListComponent } from './component/broker-parser/broker-setting-list/broker-setting-list.component';
import { AdminNemtReportComponent } from './component/superAdmin/admin-nemt-report/admin-nemt-report.component';
import { AuthTokenComponent } from './component/auth-token/auth-token.component';
import { CompaniesListComponent } from './component/companies/companies-list/companies-list.component';
import { CreateCompanyComponent } from './component/companies/create-company/create-company.component';
import { QuarterListComponent } from './component/superAdmin/quarters/quarter-list/quarter-list.component';
import { InvoiceSettingListComponent } from './component/superAdmin/invoice-settings/invoice-setting-list/invoice-setting-list.component';
import { AddressPickerComponent } from './component/shared/address-picker/address-picker.component';
import { NemtProTipsComponent } from './component/helping-material/nemt-pro-tips/nemt-pro-tips/nemt-pro-tips.component';
import { ImagesliderDirective } from './directives/image-slider/imageslider.directive';
import { SortByPipe } from './pipe/sortby-filter-pipe/sortby-filter-pipe';
import { StatisticsDetailsComponent } from './component/kpis/nemt-statistics-details/statistics-details/statistics-details.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { ManifestComponent } from './component/assigned-trips/manifest/manifest.component';
import { ConvertPhoneNumberDirective } from './directives/number-directive/convert-phone-number.directive';
import { StandingOrdersV2Component } from './component/assigned-trips/standing-orders-v2/standing-orders-v2.component';
import { OWL_DATE_TIME_FORMATS, OwlDateTimeModule } from './modules/picker/src/public_api';
import { OWL_MOMENT_DATE_TIME_ADAPTER_OPTIONS } from './modules/picker/src/lib/date-time/adapter/moment-adapter/moment-date-time-adapter.class';
import { OwlMomentDateTimeModule } from './modules/picker/src/lib/date-time/adapter/moment-adapter/moment-date-time.module';
import { TimezonePickerModule } from './modules/ng2-timezone-selector/src';

import { NemtSuperProTipsComponent } from './component/superAdmin/nemt-super-tips/nemt-super-pro-tips/nemt-super-pro-tips.component';
import { CreateTipsComponent } from './component/superAdmin/create-pro-tips/create-tips/create-tips.component';
import { StandingOrderCloneModalComponent } from './modals/standing-order-clone-modal/standing-order-clone-modal.component';
import { GanttChartComponent } from './component/assigned-trips/gantt-chart/gantt-chart.component';
import { UnAuthorizationGuard } from './service/non-auth-guard/unauthorization-guard.service';
import { StandingorderDetailModalComponent } from './modals/standingorder-detail-modal/standingorder-detail-modal/standingorder-detail-modal.component';
import { VehicleInspectionComponent } from './component/reports/inspection/vehicle-inspection/vehicle-inspection.component';
import { AddVehicleInspectionComponent } from './component/reports/inspection/add-vehicle-inspection/add-vehicle-inspection.component';
import { DailyVehicleInspectionComponent } from './component/reports/inspection/daily-vehicle-inspection/daily-vehicle-inspection/daily-vehicle-inspection.component';
import { VehicleDetailModalComponent } from './component/reports/inspection/daily-vehicle-inspection/daily-vehicle-detail-modal/vehicle-detail-modal.component';
import { TrfsTemplatesComponent } from './component/billing/trfs-templates/trfs-templates.component';
import { CtcClaimedTripsComponent } from './component/assigned-trips/offered/ctc-claimed-trips/ctc-claimed-trips.component';
import { NumericSortPipe } from './pipe/numeric-sort-pipe/numeric-sort.pipe';
import { TripGenerateModalComponent } from './modals/outstanding-generate-trip-modal/trip-generate-modal/trip-generate-modal.component';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { DriverShiftModalComponent } from './modals/driver-shift-modal/driver-shift-modal/driver-shift-modal.component';
import { DriverLoadV2Component } from './component/assigned-trips/driver-load-v2/driver-load-v2.component';
import { DriverExpensesComponent } from './component/maintenances/driver-expenses/driver-expenses.component';
import { VehicleExpensesComponent } from './component/maintenances/vehicle-expenses/vehicle-expenses.component';
import { MaintenanceDetailModalComponent } from './modals/maintenance-detail-modal/maintenance-detail-modal.component';
import { MaintenanceStatsComponent } from './component/maintenances/maintenance-stats/maintenance-stats.component';
import { FormatTitleCasePipe } from './pipe/format-titlecase.filter/format-titlecase.filter.pipe';
import { RoutingWrapperComponent } from './component/assigned-trips/outstanding/routing-wrapper/routing-wrapper.component';
import { InspectionWrapperComponent } from './component/reports/inspection/inspection-wrapper/inspection-wrapper.component';
import { PartialEditComponent } from './modals/partial-edit-modal/partial-edit.component';
import { SortByNumberPipe } from './pipe/sortby-number/sort-by-number.pipe';
import { OperationsV2Component } from './component/assigned-trips/operations-v2/operations-v2.component';
import { DispatchDriverTripsModalComponent } from './component/shared/slideouts/dispatch-driver-trips-modal/dispatch-driver-trips-modal/dispatch-driver-trips-modal.component';
import { DriverTimelinesV2Component } from './component/drivers/driver-timelines-V2/driver-timelines-v2/driver-timelines-v2.component';
import { DriverAmendmentsComponent } from './component/drivers/driver-amendments/driver-amendments/driver-amendments.component';
import { TripsMapDashboardModalComponent } from './modals/trips-map-dashboard-modal/trips-map-dashboard-modal.component';
import { TripPurposesListComponent } from './component/trip-purposes/trip-purposes-list/trip-purposes-list.component';
import { AddTripPurposesComponent } from './component/trip-purposes/trip-purposes-add/trip-purposes-add.component';
import { AddLevelOfServiceComponent } from './component/level-of-service/add-level-of-service/add-level-of-service.component';
import { LevelOfServiceComponent } from './component/level-of-service/level-of-service-list/level-of-service-list.component';
import { AddDriverAmendmentsComponent } from './component/drivers/driver-amendments/add-driver-amendments/add-driver-amendments.component';
import { AdminEarningReportComponent } from './component/superAdmin/admin-earning-report/admin-earning-report.component';
import { CompanyDriverEarningModalComponent } from './component/superAdmin/admin-earning-report/company-driver-earning-modal/company-driver-earning-modal.component';
import { DriverWrapperComponent } from './component/drivers/driver-wrapper/driver-wrapper.component';
import { DriverCredentialsComponent } from './component/drivers/driver-credentials/driver-credentials.component';
import { ServiceAreasListingComponent } from './component/service-areas/service-areas-listing/service-areas-listing.component';
import { ServiceAreasAddComponent } from './component/service-areas/service-areas-add/service-areas-add.component';
import { AddSpaceTypesComponent } from './component/level-of-service/add-space-types/add-space-types.component';
import { SpaceTypesListComponent } from './component/level-of-service/space-types-list/space-types-list.component';
import { FleetCredentialsComponent } from './component/fleet/fleet-credentials/fleet-credentials.component';
import { EquipmentListComponent } from './component/equipments/equipment-list/equipment-list.component';
import { AddEquipmentComponent } from './component/equipments/add-equipment/add-equipment.component';
import { AddPrimaryDiagnosisComponent } from './component/primary-diagnosis/add-primary-diagnosis/add-primary-diagnosis.component';
import { PrimaryDiagnosisListComponent } from './component/primary-diagnosis/primary-diagnosis-list/primary-diagnosis-list.component';
import { AddModifierCodeComponent } from './component/modifier-codes/add-modifier-code/add-modifier-code.component';
import { ModifierCodeListComponent } from './component/modifier-codes/modifier-code-list/modifier-code-list.component';
import { AddMemberWrapperModalComponent } from './component/members/add-member-wrapper-modal/add-member-wrapper-modal.component';
import { ConfigurationsComponent } from './component/corporate-user/configurations/configurations.component';
import { ConfigurationsListComponent } from './component/corporate-user/configurations-list/configurations-list.component';
import { CorporateWrapperComponent } from './component/corporate-user/corporate-wrapper/corporate-wrapper.component';
import { SmsDashboardComponent } from './component/sms/sms-dashboard/sms-dashboard.component';
import { ViolationsSetFormComponent } from './component/violations-sets/violations-set-form/violations-set-form.component';
import { ArrayFilterbykeyPipePipe } from './pipe/array-filterbykey-pipe/array-filterbykey-pipe.pipe';
import { PlannerComponent } from './component/assigned-trips/trip-planner/planner/planner.component';
import { PlannerScheduleModalComponent } from './component/assigned-trips/trip-planner/planner-schedule-modal/planner-schedule-modal.component';
import { CreateQuarterComponent } from './component/superAdmin/quarters/create-quarter/create-quarter.component';
import { BreakModalComponent } from './modals/break-modal/break-modal.component';
import { TripActivityLogComponent } from './component/assigned-trips/trip-activity-log/trip-activity-log.component';
import { PlannerScheduleLogsModalComponent } from './component/assigned-trips/trip-planner/planner-schedule-logs-modal/planner-schedule-logs-modal.component';
import { environment } from 'src/environments/environment';
import { AddonListComponent } from './component/addons/addon-list/addon-list.component';
import { AddAddonComponent } from './component/addons/add-addon/add-addon.component';
import { AddExpenseModalComponent } from './modals/add-expense-modal/add-expense-modal.component';
import { VehicleAssignModalComponent } from './modals/vehicle-assign-modal/vehicle-assign-modal.component';
import { AddAmendmentModalComponent } from './modals/add-amendment-modal/add-amendment-modal.component';
import { AdditionalNoteModalComponent } from './modals/additional-note-modal/additional-note-modal.component';
import { AdjustedTripOdomterModalComponent } from './modals/adjusted-trip-odomter-modal/adjusted-trip-odomter-modal.component';
import { AssociateImageModalComponent } from './modals/associate-image-modal/associate-image-modal.component';
import { BillerNoteModalComponent } from './modals/biller-note-modal/biller-note-modal.component';
import { SignatureModalComponent } from './modals/signature-modal/signature-modal.component';
import { AccidentalReportListComponent } from './component/reports/accidental-report/accidental-report-list/accidental-report-list.component';
import { DriverGrievanceComponent } from './component/reports/driver-grievance/driver-grievance/driver-grievance.component';
import { DriverScoreComponent } from './component/reports/driver-score/driver-score.component';
import { TripDetailsComponent } from './modals/trip-details-modal/trip-details.component';
import { StagingTripDetailModalComponent } from './modals/staging-trip-detail-modal/staging-trip-detail-modal.component';
import { StagingTripConfirmModalComponent } from './modals/staging-trip-confirm-modal/staging-trip-confirm-modal.component';
import { StagingTripFormSlideoutComponent } from './modals/staging-trip-form-slideout/staging-trip-form-slideout.component';
import { MemberTripStatsComponent } from './component/assigned-trips/staging/member-trip-stats/member-trip-stats.component';
import { MemberlinkRequestLogsModalComponent } from './modals/memberlink-request-logs-modal/memberlink-request-logs-modal.component';
import { AdjustmentComponent } from './component/billing/adjustment/adjustment.component';
import { ManualLineTripComponent } from './component/assigned-trips/manual-line-trip/manual-line-trip.component';
import { GoogleMapsInitializer } from './service/googlemap-key-service/google-maps-initializer.service';
import { TripReportsComponent } from './component/reports/trip-reports/trip-reports.component';
import { DriverLoadActionsDropdownComponent } from './component/shared/driver-load-actions-dropdown/driver-load-actions-dropdown.component';
import { BillingActionsDropdownComponent } from './component/shared/billing-actions-dropdown/billing-actions-dropdown.component';
import { EdiReportsComponent } from './component/billing/edi-parser/edi-reports/edi-reports.component';
import { AddEdiReportComponent } from './component/billing/edi-parser/add-edi-report/add-edi-report.component';
import { EdiReportModalComponent } from './modals/edi-report-modal/edi-report-modal.component';
import { DriverStatisticsComponent } from './component/reports/driver-statistics/driver-statistics.component';
import { ApplyIncentiveModalComponent } from './modals/apply-incentive-modal/apply-incentive-modal.component';
import { OutboundingComponent } from './component/reports/outbounding/outbounding.component';
import { MemberStatusModalComponent } from './modals/member-status-modal/member-status-modal.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { MemberTripsModalComponent } from './modals/member-trips-modal/member-trips-modal.component';
import { MfbComponent } from './component/miles-for-buck/mfb/mfb-list/mfb.component';
import { AddMfbComponent } from './component/miles-for-buck/mfb/add-mfb/add-mfb.component';
import { MfbDashboardComponent } from './component/miles-for-buck/mfb/mfb-dashboard/mfb-dashboard.component';
import { DriverTripsModalComponent } from './modals/driver-trips-modal/driver-trips-modal.component';
import { ChecklistComponent } from './component/spaceType-checklist/checklist/checklist.component';
import { AddChecklistComponent } from './component/spaceType-checklist/add-checklist/add-checklist.component';
import { HolidaysListComponent } from './component/extras/holidays/holidays-list/holidays-list.component';
import { AddHolidayComponent } from './component/extras/holidays/add-holiday/add-holiday.component';
import { AnnouncementListComponent } from './component/extras/announcement/announcement-list/announcement-list.component';
import { AddAnnouncementComponent } from './component/extras/announcement/add-announcement/add-announcement.component';
import { ChecklistModalComponent } from './modals/checklist-modal/checklist-modal.component';
import { FleetEquipmentsComponent } from './component/fleet/capacity-planning/fleet-equipments.component';
import { ExcelService } from './service/excel-service/excel.service';
import { DriverLoadWrapperComponent } from './component/assigned-trips/driver-load-wrapper/driver-load-wrapper.component';
import { PlannerLoadWrapperComponent } from './component/assigned-trips/trip-planner/planner-load-wrapper/planner-load-wrapper.component';

import { FilterByMultipleKeysPipe } from './pipe/filterByMultipleKeys/filter-by-multiple-keys.pipe';
import { MessageNotificationModalComponent } from './modals/message-notification-modal/message-notification-modal.component';
import { TooltipDirective } from './directives/tooltip/tooltip.directive';
import { TooltipComponent } from './component/shared/tooltip/tooltip.component';
import { AssistWrapperComponent } from './component/miles-for-buck/assist-wrapper/assist-wrapper.component';
import { AddAssistComponent } from './component/miles-for-buck/assist/add-assist/add-assist.component';
import { AssistListComponent } from './component/miles-for-buck/assist/assist-list/assist-list.component';
import { CustomTooltipDirective } from './directives/custom-tooltip/custom-tooltip.directive';
import { DashboardWrapperComponent } from './component/miles-for-buck/dashboard-wrapper/dashboard-wrapper.component';
import { AssistDashboardComponent } from './component/miles-for-buck/assist/assist-dashboard/assist-dashboard.component';
import { DriverWiseTripsComponent } from './component/assigned-trips/driver-wise-trips/driver-wise-trips.component';
import { PlannerMapperAddComponent } from './component/assigned-trips/trip-planner/planner-mapper/planner-mapper-add/planner-mapper-add.component';
import { PlannerMapperListComponent } from './component/assigned-trips/trip-planner/planner-mapper/planner-template-list/planner-mapper-list.component';
import { PlannerTemplateAddComponent } from './component/assigned-trips/trip-planner/planner-template/planner-template-add/planner-template-add.component';
import { PlannerTemplateListComponent } from './component/assigned-trips/trip-planner/planner-template/planner-template-list/planner-template-list.component';
import { PlannerTemplateSaveModalComponent } from './component/assigned-trips/trip-planner/planner-template-save-modal/planner-template-save-modal.component';
import { LateOnsceneReasonComponent } from './component/fleet/late-onscene/late-onscene-reason/late-onscene-reason.component';
import { AddLateOnsceneReasonComponent } from './component/fleet/late-onscene/add-late-onscene-reason/add-late-onscene-reason.component';
import { CallModalComponent } from './modals/call-modal/call-modal.component';
import { CallNotificationModalComponent } from './modals/call-notification-modal/call-notification-modal.component';
import { DriverScoreWrapperComponent } from './component/reports/driver-score-wrapper/driver-score-wrapper.component';
import { DriverScoreChartsComponent } from './component/reports/driver-score-charts/driver-score-charts.component';
import { PushToTalkComponent } from './component/push-to-talk/push-to-talk/push-to-talk.component';
import { CreateGroupChatModalComponent } from './modals/create-group-chat-modal/create-group-chat-modal.component';
import { PushToTalkWrapperComponent } from './component/push-to-talk/push-to-talk-wrapper/push-to-talk-wrapper.component';
import { ViewersModalComponent } from './modals/viewers-modal/viewers-modal.component';
import { AllRecordingsComponent } from './component/push-to-talk/all-recordings/all-recordings.component';
import { FeedbackComponent } from './modals/feedback/feedback.component';
import { ClaimModalComponent } from './modals/claim-modal/claim-modal.component';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { SchedulerComponent } from './component/scheduler/scheduler.component';
import { CommonModule } from '@angular/common';
import { CalendarModule, DateAdapter, CalendarDateFormatter } from 'angular-calendar';
import { SchedulerModule, SchedulerDateFormatter } from './modules/my-calendar-scheduler/public_api';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { HorizontalBarChartComponent } from './itineries/component/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { VerticalStackedBarChartComponent } from './itineries/component/charts/vertical-stacked-bar-chart/vertical-stacked-bar-chart.component';
import { RouteFilterationComponent } from './itineries/component/route-filteration/route-filteration.component';
import { RouteListingComponent } from './itineries/component/route-listing/route-listing.component';
import { SearchFilterComponent } from './itineries/component/search-filter/search-filter.component';
import { InfoModalComponent } from './itineries/modals/info-modal/info-modal.component';
import { ChartsModule } from 'ng2-charts';
import { TripsheetComponent } from './component/itineries-v2/tripsheet/tripsheet.component';
import { DriverloadComponent } from './component/itineries-v2/driverload/driverload.component';
import { ItineriesComponent } from './component/itineries-v2/itineries/itineries.component';
import { ItineriesModule } from './itineries/itineries.module';
import { WillcallActivationModalComponent } from './modals/willcall-activation-modal/willcall-activation-modal.component';
import { GanttChartV2Component } from './component/assigned-trips/gantt-chart-v2/gantt-chart-v2.component';
import { AppointmentTypeMapperListComponent } from './component/trip-purposes/mapper/appointment-type-mapper-list/appointment-type-mapper-list.component';
import { AppointmentTypeMapperAddComponent } from './component/trip-purposes/mapper/appointment-type-mapper-add/appointment-type-mapper-add.component';
import { CorporateCancelReasonComponent } from './component/corporate-user/corporate-cancel-reason/corporate-cancel-reason.component';
import { CorporateCancelReasonListComponent } from './component/corporate-user/corporate-cancel-reason-list/corporate-cancel-reason-list.component';
import { FundingsourceConfigurationComponent } from './component/corporate-user/fundingsource-configuration/fundingsource-configuration.component';
import { FundingsourceConfigurationListComponent } from './component/corporate-user/fundingsource-configuration-list/fundingsource-configuration-list.component';
import { OtpListComponent } from './component/reports/otp-list/otp-list.component';
import { DriverLoadV3Component } from './component/assigned-trips/driver-load-v3/driver-load-v3.component';
import { DriverLoadV4Component } from './component/assigned-trips/driver-load-v4/driver-load-v4.component';
import { MarkMembersAsPrimaryModalComponent } from './modals/mark-members-as-primary-modal/mark-members-as-primary-modal.component';
import { ServiceZoneAddComponent } from './component/service-zones/service-zone-add/service-zone-add.component';
import { ServiceZoneListingComponent } from './component/service-zones/service-zone-listing/service-zone-listing.component';
import { ResetPasswordComponent } from './component/reset-password/reset-password.component';
import { NotesModalComponent } from './modals/notes-modal/notes-modal/notes-modal.component';
import { TwoFactorAuthenticationComponent } from './component/two-factor-authentication/two-factor-authentication.component';
import { TwoFactorAuthenticationGuard } from './service/two-factor-guard/two-factor-guard.service';
import { NemtReportWrapperComponent } from './component/kpis/nemt-report-wrapper/nemt-report-wrapper.component';
import { NemtReportRevenueComponent } from './component/kpis/nemt-report-revenue/nemt-report-revenue.component';
import { DriverReportModalComponent } from './component/kpis/driver-report-modal/driver-report-modal.component';
import { DriverShiftOverrideComponent } from './component/drivers/shift-override/driver-shift-override/driver-shift-override.component';
import { DriverShiftOverrideListComponent } from './component/drivers/shift-override/driver-shift-override-list/driver-shift-override-list.component';
import { IntakeSuggestedTimeModalComponent } from './modals/intake-suggested-time-modal/intake-suggested-time-modal.component';
import { PossibilityAssignModalComponent } from './modals/possibility-assign-modal/possibility-assign-modal.component';
import { ZonesComponent } from './component/zones/zones/zones.component';
import { AgmDrawingModule } from '@agm/drawing';
import { PayorListComponent } from './component/payors/payor-list/payor-list.component';
import { AddPayorComponent } from './component/payors/add-payor/add-payor.component';
import { NoSpaceDirective } from './directives/nospace/no-space.directive';
import { TripSheetComponent } from './component/assigned-trips/trip-sheet/trip-sheet.component';
import { FarmoutConfigurationsListComponent } from './component/farmout-configurations/farmout-configurations-list/farmout-configurations-list.component';
import { AddFarmoutConfigurationsComponent } from './component/farmout-configurations/add-farmout-configurations/add-farmout-configurations.component';
import { AuditLogsComponent } from './component/audit-logs/audit-logs.component';
import { BulkCallReviewModalComponent } from './modals/bulk-call-review-modal/bulk-call-review-modal.component';

const ngxUiLoaderConfig: NgxUiLoaderConfig  = {
  bgsColor: 'red',
  bgsOpacity: 0.5,
  bgsPosition: 'bottom-right',
  bgsSize: 80,
  bgsType: 'ball-spin-clockwise',
  blur: 0,
  fgsColor: '#727cf5',
  fgsPosition: 'center-center',
  fgsSize: 0,
  fgsType: 'ball-spin',
  gap: 24,
  logoPosition: 'center-center',
  logoSize: 150,
  logoUrl: '',
  masterLoaderId: 'master',
  overlayBorderRadius: '0',
  overlayColor: '#ffffff',
  pbColor: '#727cf5',
  pbDirection: 'ltr',
  pbThickness: 3,
  hasProgressBar: true,
  text: '',
  textColor: '#FFFFFF',
  textPosition: 'center-center',
};

export const MY_MOMENT_FORMATS = {
  fullPickerInput: 'MM/DD/YYYY HHmm',
  parseInput: 'l LT',
  datePickerInput: 'l',
  timePickerInput: 'HH:mm',
  monthYearLabel: 'MMM YYYY',
  dateA11yLabel: 'LL',
  monthYearA11yLabel: 'MMMM YYYY',
};

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  swipeEasing: true,
  minScrollbarLength: 20
};
@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    SignInComponent,
    ForgotPasswordComponent,
    RangePipe,
    FilterPipe,
    FilterByKeyPipe,
    DispatchDashboardComponent,
    AvailabilityMatrixComponent,
    AvailabilityCoverageComponent,
    StaffMainComponent,
    StaffsCreateComponent,
    EditUserProfileComponent,
    AutocompleteComponent,
    // TripsHistoryComponent,
    DriverListComponent,
    RolePipe,
    AccountStatusPipe,
    CreateDriverComponent,
    DriverProfileDashboardComponent,
    ImageModalComponent,
    CamelCasePipe,
    CorporateuserComponent,
    CorporateAddUserComponent,
    FleetListComponent,
    CreateFleetComponent,
    DriverDistributionsComponent,
    DriverDistributionsCreateComponent,
    DriverAlertComponent,
    HelpingMaterialListComponent,
    UrlSafePipe,
    ParseTripsComponent,
    NewTripComponent,
    AutocompleteComponent,
    DispatchDashboardComponent,
    UnassignedComponent,
    StagingComponent,
    VerifiedTripLogsComponent,
    GooglePlaceDirective,
    NemtStatisticsComponent,
    NemtReportComponent,
    DynamicFareListComponent,
    DynamicFareAddComponent,
    EarningsComponent,
    TripDetailsComponent,
    StagingTripDetailModalComponent,
    StagingTripConfirmModalComponent,
    StagingTripFormSlideoutComponent,
    MemberTripStatsComponent,
    MemberlinkRequestLogsModalComponent,
    AdjustmentComponent,
    TripFilterPipe,
    TripAssignModalComponent,
    ThirdPartyCompaniesTripAssignModalComponent,
    TimeFormatDirective,
    CompanyNoteModalComponent,
    CancelTripModalComponent,
    PullBackFarmoutModalComponent,
    // ConfirmModalComponent,
    TimePipe,
    FilterByMultipleKeysPipe,
    TripDetailDialogComponent,
    BillerNoteModalComponent,
    CommonAlertComponent,
    DriverBreakdownComponent,
    SweepTripsModalComponent,
    DatePipe,
    NotificationDetailsComponent,
    // AccidnetsListComponent,
    // AddAccidentComponent,
    HelpingMaterialPdfComponent,
    // AccidentDetailInTailwindFormComponent,
    LogsComponent,
    MemberListComponent,
    CreateMemberComponent,
    OutstandingComponent,
    DriverInvoicesComponent,
    DirtRoadComponent,
    DriverTimelinesComponent,
    TripDetailModelComponent,
    OutstandingTripsModalComponent,
    // TripAdjustmentComponent,
    // CompletedCancelModalComponent,
    AdditionalNoteModalComponent,
    ZDatesComponent,
    SideNavComponent,
    NotificationsSlideoutComponent,
    DispatchInprogressTripsSlideoutComponent,
    SortByDatePipe,
    FormatTitleCasePipe,
    DriverEarningModalComponent,
    AddExpenseModalComponent,
    OutsideClickDirective,
    BrokerDropdownComponent,
    TripActionsDropdownComponent,
    AiAssistantConversationModalComponent,
    ClockTimePipe,
    SortByPipe,
    EditTripModalComponent,
    ShiftOverrideModalComponent,
    AdjustedTripOdomterModalComponent,
    OfferedComponent,
    BrokerSettingComponent,
    BrokerSettingListComponent,
    AuthTokenComponent,
    AdminNemtReportComponent,
    CompaniesListComponent,
    CreateCompanyComponent,
    QuarterListComponent,
    InvoiceSettingListComponent,
    AddressPickerComponent,
    NemtProTipsComponent,
    ImagesliderDirective,
    StatisticsDetailsComponent,
    ManifestComponent,
    ConvertPhoneNumberDirective,
    StandingOrdersV2Component,
    NemtSuperProTipsComponent,
    CreateTipsComponent,
    StandingOrderCloneModalComponent,
    GanttChartComponent,
    VehicleInspectionComponent,
    AddVehicleInspectionComponent,
    StandingorderDetailModalComponent,
    SignatureModalComponent,
    DailyVehicleInspectionComponent,
    VehicleDetailModalComponent,
    TrfsTemplatesComponent,
    CtcClaimedTripsComponent,
    NumericSortPipe,
    TripGenerateModalComponent,
    ManualLineTripComponent,
    DriverShiftModalComponent,
    AccidentalReportListComponent,
    DriverExpensesComponent,
    VehicleExpensesComponent,
    MaintenanceDetailModalComponent,
    MaintenanceStatsComponent,
    AssociateImageModalComponent,
    DriverLoadV2Component,
    DriverScoreComponent,
    DriverGrievanceComponent,
    RoutingWrapperComponent,
    InspectionWrapperComponent,
    PartialEditComponent,
    SortByNumberPipe,
    OperationsV2Component,
    DispatchDriverTripsModalComponent,
    DriverTimelinesV2Component,
    DriverAmendmentsComponent,
    AddAmendmentModalComponent,
    TripsMapDashboardModalComponent,
    TripPurposesListComponent,
    AddTripPurposesComponent,
    AddLevelOfServiceComponent,
    LevelOfServiceComponent,
    AddDriverAmendmentsComponent,
    AdminEarningReportComponent,
    CompanyDriverEarningModalComponent,
    DriverWrapperComponent,
    DriverCredentialsComponent,
    ServiceAreasListingComponent,
    ServiceAreasAddComponent,
    AddSpaceTypesComponent,
    SpaceTypesListComponent,
    FleetCredentialsComponent,
    EquipmentListComponent,
    AddEquipmentComponent,
    AddPrimaryDiagnosisComponent,
    PrimaryDiagnosisListComponent,
    AddModifierCodeComponent,
    ModifierCodeListComponent,
    AddMemberWrapperModalComponent,
    ConfigurationsComponent,
    ConfigurationsListComponent,
    CorporateWrapperComponent,
    SmsDashboardComponent,
    ViolationsSetFormComponent,
    VehicleAssignModalComponent,
    ArrayFilterbykeyPipePipe,
    PlannerComponent,
    PlannerScheduleModalComponent,
    PlannerScheduleLogsModalComponent,
    CreateQuarterComponent,
    BreakModalComponent,
    TripActivityLogComponent,
    AddonListComponent,
    AddAddonComponent,
    TripReportsComponent,
    DriverLoadActionsDropdownComponent,
    BillingActionsDropdownComponent,
    EdiReportsComponent,
    AddEdiReportComponent,
    EdiReportModalComponent,
    DriverStatisticsComponent,
    ApplyIncentiveModalComponent,
    OutboundingComponent,
    MemberStatusModalComponent,
    MemberTripsModalComponent,
    MfbComponent,
    AddMfbComponent,
    MfbDashboardComponent,
    DriverTripsModalComponent,
    ChecklistComponent,
    AddChecklistComponent,
    HolidaysListComponent,
    AddHolidayComponent,
    AnnouncementListComponent,
    AddAnnouncementComponent,
    ChecklistModalComponent,
    FleetEquipmentsComponent,
    DriverLoadWrapperComponent,
    PlannerLoadWrapperComponent,
    MessageNotificationModalComponent,
    TooltipDirective,
    TooltipComponent,
    AssistWrapperComponent,
    AddAssistComponent,
    AssistListComponent,
    CustomTooltipDirective,
    DashboardWrapperComponent,
    AssistDashboardComponent,
    DriverLoadWrapperComponent,
    PlannerLoadWrapperComponent,
    DriverWiseTripsComponent,
    PlannerMapperAddComponent,
    PlannerMapperListComponent,
    PlannerTemplateAddComponent,
    PlannerTemplateListComponent,
    PlannerTemplateSaveModalComponent,
    LateOnsceneReasonComponent,
    AddLateOnsceneReasonComponent,
    CallModalComponent,
    CallNotificationModalComponent,
    DriverScoreWrapperComponent,
    DriverScoreChartsComponent,
    PushToTalkComponent,
    CreateGroupChatModalComponent,
    AllRecordingsComponent,
    PushToTalkWrapperComponent,
    ViewersModalComponent,
    FeedbackComponent,
    ClaimModalComponent,
    SchedulerComponent,
    ItineriesComponent,
    DriverloadComponent,
    TripsheetComponent,
    WillcallActivationModalComponent,
    GanttChartV2Component,
    AppointmentTypeMapperListComponent,
    AppointmentTypeMapperAddComponent,
    CorporateCancelReasonComponent,
    CorporateCancelReasonListComponent,
    FundingsourceConfigurationComponent,
    FundingsourceConfigurationListComponent,
    OtpListComponent,
    DriverLoadV3Component,
    DriverLoadV4Component,
    MarkMembersAsPrimaryModalComponent,
    ServiceZoneListingComponent,
    ServiceZoneAddComponent,
    ResetPasswordComponent,
    NotesModalComponent,
    TwoFactorAuthenticationComponent,
    NemtReportWrapperComponent,
    NemtReportRevenueComponent,
    DriverReportModalComponent,
    DriverShiftOverrideComponent,
    DriverShiftOverrideListComponent,
    IntakeSuggestedTimeModalComponent,
    PossibilityAssignModalComponent,
    ZonesComponent,
    PayorListComponent,
    AddPayorComponent,
    TripSheetComponent,
    NoSpaceDirective,
    FarmoutConfigurationsListComponent,
    AddFarmoutConfigurationsComponent,
    AuditLogsComponent,
    BulkCallReviewModalComponent,
    ],
  entryComponents: [TooltipComponent],

  imports: [
    CommonModule,
    BrowserModule,
    FormsModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SignaturePadModule,
    ReactiveFormsModule,
    TimeagoModule.forRoot(),
    HttpClientModule, // import HttpClientModule
    NgxUiLoaderModule.forRoot(ngxUiLoaderConfig), // import NgxUiLoaderModule
    NgxUiLoaderHttpModule.forRoot({ exclude:['http://provider.qalbs.com/api/assigns/dashboard', 'http://provider.qalbs.com/api/notifications', 'http://provider.qalbs.com/api/assigns/corporate',
    'http://provider.qalbs.com/api/users/me',  'http://provider.qalbs.com/api/drivers/nearby'],
     showForeground: true }),
     CircleTimerModule,
    AgmCoreModule.forRoot({
      apiKey: "",
      apiVersion: '3.64',
      libraries: ['places', 'drawing']
    }),
    AgmDrawingModule,
    AgmOverlays,
    AgmDirectionModule,
    SocketIoModule,
    TimezonePickerModule,
    BrowserAnimationsModule,
    OwlDateTimeModule,
    OwlMomentDateTimeModule,
    PerfectScrollbarModule,
    OwlModule,
    ToastrModule.forRoot({
    timeOut: 3000,
    positionClass: 'toast-top-right',
    preventDuplicates: true,
    progressBar:true
    }),
    NgSelectModule,
    NgMultiSelectDropDownModule.forRoot(),
    NgxPaginationModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory
    }),
    SchedulerModule.forRoot({ locale: 'en', headerDateFormat: 'daysRange', logEnabled: true }),
    DragDropModule,
    BrowserAnimationsModule,
    ChartsModule,
    ItineriesModule,
    I18nModule
  ],

  providers: [
    { provide: LOCALE_ID, useValue: 'en-US' },
  {
      provide: CalendarDateFormatter,
      useClass: SchedulerDateFormatter
  },
    {
      provide: APP_INITIALIZER,
      deps: [LAZY_MAPS_API_CONFIG, GoogleMapsInitializer],
      multi: true,
      useFactory: (config: LazyMapsAPILoaderConfigLiteral, initializer: GoogleMapsInitializer) => () => initializer.initialize(config),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptorService,
      multi: true
    },
    { provide: AuthorizationGuard, useClass: AuthorizationGuard },
    { provide: UnAuthorizationGuard, useClass: UnAuthorizationGuard },
    { provide: TwoFactorAuthenticationGuard, useClass: TwoFactorAuthenticationGuard },
    { provide: OWL_DATE_TIME_FORMATS, useValue: MY_MOMENT_FORMATS },
    { provide: OWL_MOMENT_DATE_TIME_ADAPTER_OPTIONS, useValue: { useUtc: false } },
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
    },
    DatePipe,
    FilterByMultipleKeysPipe,
    FormatTitleCasePipe,
    MySocketWrapper,
    SocketService,
    ExcelService,
    CamelCasePipe,


  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
